import React from "react";

import { LinkSubBlock as Value } from "@reactivated";

import {
    ButtonPrimary,
    ButtonSecondary,
    Clickable,
    TextLink,
} from "../Clickable";
import { ClickableRefAttributes } from "../Clickable/types";

export type LinkSubBlockClickableMap<T extends string> = {
    [K in T]: React.ComponentType<ClickableRefAttributes>;
};

/**
 * The name and number of clickables in a project's design does not need to be
 * the same all the time. Child projects should be able to subclass LinkSubBlock
 * and provide a new set of choices.
 *
 * This allows a child project to create clickableMap using the subclass's
 * generated interface, and uses that to make sure that something in the child
 * project will error if `clickableMap` doesn't matck up with `value`.
 *
 * If someone finds a better way to solve this problem, please change this.
 */
export const createLinkSubBlockComponent = <T extends string>(
    clickableMap: LinkSubBlockClickableMap<T>,
) => {
    type LinkSubBlockOverridable = Omit<Value, "link_style"> & {
        link_style?: T | null;
    };

    type LinkSubBlockComponentProps =
        React.AnchorHTMLAttributes<HTMLAnchorElement> & {
            value: LinkSubBlockOverridable;
            unstyled?: boolean;
            onDark?: boolean;
        };

    const LinkSubBlockComponent = (
        {
            value,
            unstyled,
            onDark = false,
            ...props
        }: React.PropsWithChildren<LinkSubBlockComponentProps>,
        ref: React.Ref<HTMLAnchorElement>,
    ) => {
        const target = value.link_type === "external" ? "_blank" : "_self";
        const children = props.children || value.display_name;
        const link_style = unstyled ? "unstyled" : value.link_style;
        let Component: React.ComponentType<ClickableRefAttributes> | null =
            null;
        if (link_style !== null && link_style !== undefined) {
            Component = clickableMap[link_style];
        }

        return Component ? (
            <Component
                {...props}
                onDark={onDark}
                href={value.url || undefined}
                ref={ref}
                target={target}
            >
                {children}
            </Component>
        ) : (
            <></>
        );
    };

    return React.forwardRef(LinkSubBlockComponent);
};

export type LinkStyle = NonNullable<Value["link_style"] | "unstyled">;

export const clickableMap: LinkSubBlockClickableMap<LinkStyle> = {
    "": ButtonPrimary,
    "secondary": ButtonSecondary,
    "text-link": TextLink,
    "unstyled": Clickable,
};

export const LinkSubBlock = createLinkSubBlockComponent(clickableMap);
